import { setLightness } from "polished";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "styled-components";
import { Text } from "../../../../../../../components/common";
import ButtonComponent from "../../../../../../../components/onboarding/button";
import InputComponent from "../../../../../../../components/onboarding/input";
import useRootTranslation from "../../../../../../../hooks/use-root-translation";
import { AppState } from "../../../../../../../store/app-state";
import {
  checkEmailDomain,
  fetchInvites,
  sendInvite,
} from "../../../../../../../store/company-profile/company-profile.actions";
import { ModalContainer } from "./add-people-modal.styled";

const singUpLink = (token: string, slug: string) => {
  return `https://www.dio.me/talent-match/secure/sign-up/${slug}?invite_token=${token}`;
};

const AddPeopleModal: React.FC<{ initial_email: string }> = ({
  initial_email,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [email, setEmail] = useState<string>(initial_email);
  const [inviteToken, setInviteToken] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [invalidDomain, setInvalidDomain] = useState<boolean>(false);
  const { user } = useSelector((state: AppState) => state.authState);
  const { root } = useRootTranslation(
    "company-profile.group-section.add-people-modal",
    "private-home-page"
  );

  const domain = useMemo(() => {
    if (user?.email) return user.email.split("@")[1];
    return null;
  }, [user]);

  const handleSendInvite = useCallback(
    async (send_email: string) => {
      await sendInvite(send_email)
        .then((resp) => {
          setEmail("");
          setInviteToken(resp.token);
          if (user?.corporate.id) {
            dispatch(fetchInvites(user?.corporate.id));
          }
        })
        .catch(() => {})
        .finally(() => {
          setLoading(false);
        });
    },
    [dispatch, user]
  );

  const handleInvite = useCallback(async () => {
    setLoading(true);
    const send_email = `${email}@${domain}`;
    await checkEmailDomain(send_email)
      .then((resp) => {
        setInvalidDomain(!resp.valid);
        if (resp.valid) {
          handleSendInvite(send_email);
        } else {
          setLoading(false);
        }
      })
      .catch(() => {
        setLoading(false);
      });
  }, [email, domain, handleSendInvite]);

  const handleCopy = useCallback(() => {
    const slug = `${user?.corporate.name?.toLowerCase().replace(/[/ ]/g, "-")}`;
    navigator.clipboard.writeText(singUpLink(inviteToken, slug));
    setCopied(true);
  }, [inviteToken, user?.corporate.name]);

  const hasToken = useMemo(() => {
    return inviteToken.length > 0;
  }, [inviteToken]);

  const color = theme.colors.primaryBlue.main;

  return (
    <ModalContainer>
      <Text color={color} fontSize={18} fontWeight={700}>
        {invalidDomain
          ? root("invalid-domain.title")
          : !hasToken
          ? root("not-has-token.title")
          : root("has-token.title")}
      </Text>
      {invalidDomain ? (
        <Text color={color} fontWeight={500}>
          {root("invalid-domain.description")}
        </Text>
      ) : !hasToken ? (
        <InputComponent
          value={email}
          onChange={setEmail}
          placeholder=""
          color={color}
          suffix={`@${domain}`}
          disabled={loading}
        />
      ) : (
        <Text color={color} fontWeight={700}>
          {root("has-token.invite-link")}
        </Text>
      )}
      {!invalidDomain &&
        (!hasToken ? (
          <ButtonComponent
            text={
              loading
                ? root("not-has-token.button-load")
                : root("not-has-token.button")
            }
            onClick={handleInvite}
            color={color}
            disabled={loading || email.length < 2}
          />
        ) : (
          <ButtonComponent
            text={root(`has-token.button-${copied ? "copied" : "copy"}`)}
            onClick={handleCopy}
            color={setLightness(0.6, theme.colors.primaryBlue.main)}
            disabled={copied}
          />
        ))}
    </ModalContainer>
  );
};

export default AddPeopleModal;
